/**
 * Permission catalogue — single source of truth per [[0001-architecture]] hard rule 7.
 *
 * Two parts:
 *   1. Static keys (`STATIC_PERMISSIONS`) — fixed strings, closed union via `keyof typeof`.
 *   2. Dynamic per-group keys — `devicefiles:<slug>:upload|delete` derived at runtime
 *      from `device_file_groups.slug`. Use the helpers below; do not construct by hand.
 *
 * The API must reject any `role_permissions` write whose key is neither a static key
 * nor matches `^devicefiles:<existing-slug>:(upload|delete)$` nor
 * `^releases:<existing-product-slug>:<env>:(view|create|confirm|publish)$` (UCH-419).
 */

import { RELEASE_ENVIRONMENTS, type ReleaseEnvironment } from './api/release-products/shapes';

export const STATIC_PERMISSIONS = {
  'users:list': {
    label: 'View users',
    description: 'See the user list and open user detail pages.',
  },
  'users:invite': {
    label: 'Invite users',
    description: 'Pre-create an allowlist entry by email so that person can sign in.',
  },
  'users:remove': {
    label: 'Remove users',
    description: 'Delete a user from the workspace.',
  },
  'users:changeRole': {
    label: 'Change user roles',
    description: 'Assign or remove roles and user groups; last-admin protection still applies.',
  },
  'users:editGithub': {
    label: 'Edit GitHub usernames',
    description: "Edit or lock any user's GitHub username mapping.",
  },
  'users:disable': {
    label: 'Disable users',
    description: 'Disable or re-enable a user; disabled users cannot sign in or use the API.',
  },
  'users:resetWelcome': {
    label: 'Reset welcome',
    description:
      "Reset a user's first-entry welcome so the celebration plays again on their next visit.",
  },

  'roles:list': {
    label: 'View roles',
    description: 'See the role catalogue and each role’s permissions.',
  },
  'roles:create': {
    label: 'Create roles',
    description: 'Create custom roles with any permission set.',
  },
  'roles:update': {
    label: 'Edit roles',
    description: 'Rename custom roles and change their permissions. System roles stay locked.',
  },
  'roles:delete': {
    label: 'Delete roles',
    description: 'Delete custom roles that no user currently holds.',
  },

  'groups:list': {
    label: 'View user groups',
    description: 'Read the user-group catalogue (needed to render group chips).',
  },
  'groups:create': {
    label: 'Create user groups',
    description: 'Add new user groups to the catalogue.',
  },
  'groups:update': {
    label: 'Edit user groups',
    description: 'Rename/update user groups, including restoring soft-deleted ones.',
  },
  'groups:delete': {
    label: 'Delete user groups',
    description: 'Soft-delete user groups from the catalogue.',
  },
  'groups:assignMembers': {
    label: 'Assign group members',
    description: 'Add or remove members of a user group.',
  },

  'devices:list': {
    label: 'View devices',
    description: 'See the device inventory and device detail pages.',
  },
  'devices:register': {
    label: 'Register devices',
    description: 'Register a new device (IMEI required without the "without IMEI" grant).',
  },
  'devices:registerWithoutImei': {
    label: 'Register without IMEI',
    description: 'Allow registering a device with an empty IMEI.',
  },
  'devices:edit': {
    label: 'Edit devices',
    description: 'Edit device details.',
  },
  'devices:delete': {
    label: 'Delete devices',
    description: 'Remove a device from the inventory.',
  },
  'devices:assign': {
    label: 'Assign devices',
    description: 'Assign or unassign a device to a user.',
  },
  'devices:editOwnNote': {
    label: 'Edit own device note',
    description: 'Edit the note on devices assigned to yourself.',
  },
  'devices:searchByImei': {
    label: 'Search by IMEI',
    description: 'IMEI search over unassigned devices only, unless the "(all)" grant is also held.',
  },
  'devices:searchByImei:all': {
    label: 'Search by IMEI (all)',
    description: 'IMEI search also returns assigned devices, including assignee names.',
  },

  'devices:files:groups:manage': {
    label: 'Manage device file groups',
    description: 'Create, edit, and delete the workspace-wide device file groups.',
  },
  'devices:files:accessAll': {
    label: 'Access all device files',
    description: 'Upload/delete device files on devices not assigned to yourself.',
  },

  'polls:list': {
    label: 'View polls',
    description: 'See open and closed polls in the workspace.',
  },
  'polls:create': {
    label: 'Create polls',
    description: 'Create and publish survey polls.',
  },
  'polls:respond': {
    label: 'Respond to polls',
    description: 'Submit, edit, or withdraw your own poll responses.',
  },
  'polls:close': {
    label: 'Close own polls',
    description: 'Close polls you created.',
  },
  'polls:delete': {
    label: 'Delete any poll',
    description: 'Delete any poll and all of its responses.',
  },
  'polls:viewAllResults': {
    label: 'View all poll results',
    description: 'View results for every poll regardless of its visibility setting.',
  },

  'releases:view': {
    label: 'View releases',
    description: 'See releases, changelogs, and release detail pages.',
  },
  'releases:drafts:create': {
    label: 'Create release drafts',
    description: 'Start a release draft and pick the git ref to build from.',
  },
  'releases:confirm': {
    label: 'Confirm releases',
    description: 'Confirm a draft, freezing its changelog and dispatching the build.',
  },
  'releases:publish': {
    label: 'Publish releases',
    description: 'Publish a confirmed release to users.',
  },
  'releases:plans:manage': {
    label: 'Manage release plans',
    description: 'Create and edit release plans and templates.',
  },
  'releases:config:manage': {
    label: 'Manage release config',
    description: 'Edit product operational config such as the next build number.',
  },
  'releases:products:manage': {
    label: 'Manage release products',
    description: 'Disable or enable release products.',
  },
  'releases:secrets:view': {
    label: 'View release secrets',
    description: 'Reveal the Confirm-time upload token and API URL (audited).',
  },
  'releases:udids:manage': {
    label: 'Manage Apple UDIDs',
    description: "View all users' Apple UDIDs and dispatch iOS ad hoc device registration.",
  },

  'runners:view': {
    label: 'View CI runners',
    description: 'Open the read-only runner monitor: machines, runners, job queue and history.',
  },
  'runners:machines:manage': {
    label: 'Manage runner machines',
    description: 'Add or remove monitored machines and issue or rotate their agent tokens.',
  },
} as const;

export type StaticPermissionKey = keyof typeof STATIC_PERMISSIONS;

export type GroupFilePermissionKey =
  | `devicefiles:${string}:upload`
  | `devicefiles:${string}:delete`;

export type ReleaseScopedCap = 'view' | 'create' | 'confirm' | 'publish';
export type ReleaseScopePermissionKey =
  `releases:${string}:${ReleaseEnvironment}:${ReleaseScopedCap}`;

export type PermissionKey =
  | StaticPermissionKey
  | GroupFilePermissionKey
  | ReleaseScopePermissionKey;

export const STATIC_PERMISSION_KEYS = Object.keys(STATIC_PERMISSIONS) as StaticPermissionKey[];

export const groupUploadPerm = (slug: string): GroupFilePermissionKey =>
  `devicefiles:${slug}:upload`;
export const groupDeletePerm = (slug: string): GroupFilePermissionKey =>
  `devicefiles:${slug}:delete`;

const GROUP_PERM_PATTERN = /^devicefiles:([a-z][a-z0-9-]{1,39}):(upload|delete)$/;

export const parseGroupFilePermissionKey = (
  key: string,
): { slug: string; action: 'upload' | 'delete' } | null => {
  const match = GROUP_PERM_PATTERN.exec(key);
  if (!match) return null;
  return { slug: match[1] as string, action: match[2] as 'upload' | 'delete' };
};

export const isStaticPermissionKey = (key: string): key is StaticPermissionKey =>
  Object.hasOwn(STATIC_PERMISSIONS, key);

/**
 * Per-product+environment release grants (UCH-419). A scoped key narrows one of
 * the four release capabilities to a single product+environment pair; the
 * corresponding global key keeps meaning "all products/envs".
 */
export const RELEASE_SCOPED_CAPS = ['view', 'create', 'confirm', 'publish'] as const;

export const RELEASE_CAP_GLOBAL_KEY: Record<ReleaseScopedCap, StaticPermissionKey> = {
  view: 'releases:view',
  create: 'releases:drafts:create',
  confirm: 'releases:confirm',
  publish: 'releases:publish',
};

export const releaseScopePerm = (
  slug: string,
  env: ReleaseEnvironment,
  cap: ReleaseScopedCap,
): ReleaseScopePermissionKey => `releases:${slug}:${env}:${cap}`;

const RELEASE_SCOPE_PATTERN = new RegExp(
  `^releases:([a-z][a-z0-9-]{1,39}):(${RELEASE_ENVIRONMENTS.join('|')}):(view|create|confirm|publish)$`,
);

export const parseReleaseScopePermissionKey = (
  key: string,
): { slug: string; env: ReleaseEnvironment; cap: ReleaseScopedCap } | null => {
  const match = RELEASE_SCOPE_PATTERN.exec(key);
  if (!match) return null;
  return {
    slug: match[1] as string,
    env: match[2] as ReleaseEnvironment,
    cap: match[3] as ReleaseScopedCap,
  };
};

/** Global key, or the exact scoped key for this product+environment. */
export const canReleaseScope = (
  has: (key: string) => boolean,
  cap: ReleaseScopedCap,
  slug: string,
  env: ReleaseEnvironment,
): boolean => has(RELEASE_CAP_GLOBAL_KEY[cap]) || has(releaseScopePerm(slug, env, cap));

/** Global key, or any scoped key of this capability (optionally for one product). */
export const hasAnyReleaseCap = (
  perms: ReadonlySet<string>,
  cap: ReleaseScopedCap,
  slug?: string,
): boolean => {
  if (perms.has(RELEASE_CAP_GLOBAL_KEY[cap])) return true;
  for (const key of perms) {
    const parsed = parseReleaseScopePermissionKey(key);
    if (parsed && parsed.cap === cap && (slug === undefined || parsed.slug === slug)) return true;
  }
  return false;
};

/** All (slug, env) pairs granted for a capability via scoped keys. */
export const scopedReleasePairs = (
  perms: ReadonlySet<string>,
  cap: ReleaseScopedCap,
): { slug: string; env: ReleaseEnvironment }[] => {
  const pairs: { slug: string; env: ReleaseEnvironment }[] = [];
  for (const key of perms) {
    const parsed = parseReleaseScopePermissionKey(key);
    if (parsed && parsed.cap === cap) pairs.push({ slug: parsed.slug, env: parsed.env });
  }
  return pairs;
};

/**
 * Validates that a key is a static catalogue entry, a dynamic group-file key
 * whose slug appears in the known group slugs, or a scoped release key whose
 * slug appears in the known product slugs.
 */
export const isValidPermissionKey = (
  key: string,
  knownGroupSlugs: ReadonlySet<string>,
  knownProductSlugs: ReadonlySet<string> = new Set(),
): boolean => {
  if (isStaticPermissionKey(key)) return true;
  const parsed = parseGroupFilePermissionKey(key);
  if (parsed !== null) return knownGroupSlugs.has(parsed.slug);
  const scoped = parseReleaseScopePermissionKey(key);
  return scoped !== null && knownProductSlugs.has(scoped.slug);
};

/**
 * Seeded permission sets for the two system roles. The dynamic devicefiles:<slug>:* keys
 * for the seeded `photos`, `box-photos` and `receipts` groups are included so the API can
 * grant them during the role-permissions seed migration.
 */
export const SYSTEM_ROLE_PERMISSIONS: {
  admin: PermissionKey[];
  member: PermissionKey[];
} = {
  admin: [
    'users:list',
    'users:invite',
    'users:remove',
    'users:changeRole',
    'users:editGithub',
    'users:disable',
    'users:resetWelcome',
    'roles:list',
    'roles:create',
    'roles:update',
    'roles:delete',
    'groups:list',
    'groups:create',
    'groups:update',
    'groups:delete',
    'groups:assignMembers',
    'devices:list',
    'devices:register',
    'devices:registerWithoutImei',
    'devices:edit',
    'devices:delete',
    'devices:assign',
    'devices:editOwnNote',
    'devices:searchByImei',
    'devices:searchByImei:all',
    'devices:files:groups:manage',
    'devices:files:accessAll',
    'polls:list',
    'polls:create',
    'polls:respond',
    'polls:close',
    'polls:delete',
    'polls:viewAllResults',
    'releases:view',
    'releases:drafts:create',
    'releases:confirm',
    'releases:publish',
    'releases:plans:manage',
    'releases:config:manage',
    'releases:products:manage',
    'releases:secrets:view',
    'releases:udids:manage',
    'runners:view',
    'runners:machines:manage',
    groupUploadPerm('photos'),
    groupDeletePerm('photos'),
    groupUploadPerm('box-photos'),
    groupDeletePerm('box-photos'),
    groupUploadPerm('receipts'),
    groupDeletePerm('receipts'),
  ],
  member: [
    'roles:list',
    'groups:list',
    'devices:list',
    'devices:register',
    'devices:editOwnNote',
    'devices:searchByImei',
    'polls:list',
    'polls:create',
    'polls:respond',
    'polls:close',
    'runners:view',
    groupUploadPerm('photos'),
    groupDeletePerm('photos'),
  ],
};

export type SystemRoleName = 'admin' | 'member';

export const SYSTEM_ROLE_NAMES: readonly SystemRoleName[] = ['admin', 'member'] as const;
